import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { get, isEqual } from 'lodash';
import { submit, touch, untouch, change as changeFieldValue } from 'redux-form';
import { compose, pure, withHandlers, withProps } from 'recompose';
import moment from 'moment';

import { withEscHandler } from 'shared/hocs/modalWithEscHandler';
import requestsStatuses from 'modules/common/constants/requestsStatuses';
import { withRef } from 'shared/hocs/withRef';
import { scrollToFirstError } from 'shared/utils/formTools';
import { formatPhoneNumber, parsePhoneNumber } from 'modules/user/utils/formatPhoneNumber';
import createValidator from 'shared/utils/formValidator';

import {
  EMPTY_DATE_OF_BIRTH_LOCAL,
  EMPTY_USER_EMAIL_LOCAL,
  EMPTY_USER_LAST_NAME_LOCAL,
  EMPTY_USER_NAME_LOCAL,
  EMPTY_USER_PHONE_LOCAL,
  INVALID_DATE_OF_BIRTH_LOCAL,
  INVALID_EXTERNAL_ID,
  INVALID_FORMAT_USER_ADDITIONAL_PHONE_LOCAL,
  INVALID_FORMAT_USER_EMAIL_LOCAL,
  INVALID_FORMAT_USER_PHONE_LOCAL,
  localErrorMessagesMap,
} from 'modules/user/constants/localErrorCodes';
import { EMPTY_TEXT_FIELD, EMPTY_SELECT_FIELD } from 'modules/appointmentsManagement/constants/localErrorCodes';
import { userAttributeLabels } from 'modules/user/constants/userAttributeLabels';
import { getUsersReadPermissions, getUsersUpdatePermissions } from 'modules/session/selectors/permissions';
import { closeEditUserModal, invokeConfirmCancelModal } from 'modules/user/actions/invokeModals';
import { submitEditUserForm } from 'modules/user/actions/editUser';
import { userTitles, userTitlesMessages } from 'modules/user/constants/userTitles';
import { userGenderOptions, userGenderMessages } from 'modules/user/constants/userGender';
import { getCommonUserConfig } from 'modules/user/selectors/users';
import { UPDATE_USER_FORM } from 'modules/user/constants/formTypes';

import EditUserModal from './EditUserModal';

const mapStateToProps = state => {
  const userDetails = state.auth.userInfo.profile;
  const birthDate = userDetails.dateOfBirth ? Number(userDetails.dateOfBirth) : null;
  const userReadPermissions = getUsersReadPermissions(state);
  const userUpdatePermissions = getUsersUpdatePermissions(state);

  return {
    form: UPDATE_USER_FORM,
    isSubmittingForm: state.user.userCRUD.submitRequestStatus === requestsStatuses.pending,
    initialValues: {
      ...userDetails,
      dateOfBirth: moment(birthDate).isValid() ? moment(birthDate).format('DD/MM/YYYY') : '',
      phoneNumber: parsePhoneNumber(userDetails.phoneNumber)
    },
    formValues: get(state, `form[${ UPDATE_USER_FORM }].values`),
    userReadPermissions,
    userUpdatePermissions,
    userFieldConfig: getCommonUserConfig(state),
  };
};

const submitAction = () => submit(UPDATE_USER_FORM);

export default compose(
  connect(
    mapStateToProps,
    {
      submitEditUserForm,
      changeFieldValue,
      submitAction,
      untouch,
      touch,
      invokeConfirmCancelModal,
      closeEditUserModal,
    },
  ),

  withRef,
  injectIntl,

  withProps(({ intl }) => ({
    userTitleOptions: userTitles.map(item => ({
      key: item,
      value: intl.formatMessage(userTitlesMessages[item]),
    })),

    userGenderOptions: userGenderOptions.map(item => ({
      key: item,
      value: intl.formatMessage(userGenderMessages[item]),
    })),
  })),

  withHandlers({
    handleScrollToFirstError: ({ form, touch, getRef }) => (errors) => {
      scrollToFirstError(errors, getRef, (error) => touch(form, [error]));
    },

    handleCancelClick: ({ initialValues, formValues, closeEditUserModal, invokeConfirmCancelModal }) => () =>
      isEqual(formValues, initialValues)
        ? closeEditUserModal()
        : invokeConfirmCancelModal(),

    submitForm: ({ submitEditUserForm, handleScrollToFirstError }) => (formData) => {
      const data = {
        ...formData,
        firstName: formData.firstName && formData.firstName.trim(),
        middleName: formData.middleName && formData.middleName.trim(),
        lastName: formData.lastName && formData.lastName.trim(),
        policyId: formData.policyId && formData.policyId.trim(),
        address: formData.address && formData.address.trim(),
        description: formData.description && formData.description.trim(),
        dateOfBirth: formData.dateOfBirth ? moment.utc(formData.dateOfBirth, 'D/M/YYYY', true).valueOf() : null,
        phoneNumber: formatPhoneNumber(formData.phoneNumber),
      };

      return submitEditUserForm(data, handleScrollToFirstError);
    },
    validate: ({ intl, userFieldConfig = {} }) => (formProps) => {
      const rules = {
        'firstName': ['string', 'max:200'],
        'lastName': ['string', 'max:200'],
        'middleName': ['max:200'],
        'title': [],
        'gender': [],
        'dateOfBirth': ['dateOfBirth'],
        'type': ['required'],
        'email': ['email'],
        'phoneNumber': [`telephone:${ userFieldConfig.userPhoneCountryCode }`],
        'externalId': [`regex:/${ userFieldConfig.externalIdRegexp }/`],
        'address': [],
        'description': [],
      };

      get(userFieldConfig, 'requiredFields', []).forEach(field => {
        if (rules[field]) rules[field].unshift('required');
      });

      return createValidator(rules, {
        'required.firstName': `${ EMPTY_USER_NAME_LOCAL }`,
        'required.middleName': localErrorMessagesMap[EMPTY_TEXT_FIELD],
        'required.lastName': `${ EMPTY_USER_LAST_NAME_LOCAL }`,
        'required.title': localErrorMessagesMap[EMPTY_SELECT_FIELD],
        'required.gender': localErrorMessagesMap[EMPTY_SELECT_FIELD],
        'required.email': `${ EMPTY_USER_EMAIL_LOCAL }`,
        'required.phoneNumber': `${ EMPTY_USER_PHONE_LOCAL }`,
        'required.externalId': localErrorMessagesMap[EMPTY_TEXT_FIELD],
        'required.address': localErrorMessagesMap[EMPTY_TEXT_FIELD],
        'required.description': localErrorMessagesMap[EMPTY_TEXT_FIELD],
        'required.dateOfBirth': localErrorMessagesMap[EMPTY_DATE_OF_BIRTH_LOCAL],
        'invalid.dateOfBirth': `${ INVALID_DATE_OF_BIRTH_LOCAL }`,
        'invalid.email': `${ INVALID_FORMAT_USER_EMAIL_LOCAL }`,
        'invalid.phoneNumber': `${ INVALID_FORMAT_USER_PHONE_LOCAL }`,
        'regex.externalId': `${ INVALID_EXTERNAL_ID }`,
      }, { externalId: userFieldConfig.externalIdLabel, ...userAttributeLabels })(formProps, { intl });
    } }),

  withEscHandler,
  pure,
)(EditUserModal);
